import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import PriorityListModal from "../shared/PriorityListModal";

/* ── Navigation structure (Ai4-style with submenus) ── */
const NAV_ITEMS = [
  {
    label: "Agenda",
    children: [
      { label: "Agenda Overview", href: "/program" },
      { label: "Full Agenda", href: "/program#agenda" },
      { label: "Tracks", href: "/program#pathways" },
      { label: "Workshops & Labs", href: "/program#workshops" },
      { label: "Speaker Directory", href: "/speakers" },
      { label: "Apply to Speak", href: "/speakers#apply" },
    ],
  },
  {
    label: "Attendees",
    children: [
      { label: "Why Attend", href: "/attend" },
      { label: "Ticket Types", href: "/attend#tickets" },
      { label: "Networking & Connect", href: "/connect" },
      { label: "Plan Your Visit", href: "/plan" },
      { label: "FAQ", href: "/faq" },
    ],
  },
  {
    label: "Expo",
    children: [
      { label: "Expo Overview", href: "/expo" },
      { label: "Exhibitor Packages", href: "/expo#booth-types" },
      { label: "Startup Zone", href: "/expo#startups" },
      { label: "Innovation Showcase", href: "/expo#categories" },
    ],
  },
  {
    label: "Sponsors",
    children: [
      { label: "Sponsor Opportunities", href: "/partner" },
      { label: "Sponsorship Tiers", href: "/partner#sponsor-tiers" },
      { label: "Request Prospectus", href: "/partner#prospectus" },
    ],
  },
  {
    label: "About Us",
    children: [
      { label: "About Core AI Summit", href: "/about" },
      { label: "Why Niagara Falls", href: "/plan" },
      { label: "News & Announcements", href: "/news" },
      { label: "About the Founder", href: "/about#founder" },
      { label: "Contact Us", href: "/faq" },
    ],
  },
];

const LOGO_URL = "https://customer-assets-0z36b82j.emergentagent.net/job_6bb71053-4e67-4930-bf10-a7af9cda97ea/artifacts/5ia3fp43_core-ai-summit-logo-on-black.png";

const CoreAILogo = () => (
  <Link to="/" className="flex items-center flex-shrink-0" data-testid="nav-logo">
    <img
      src={LOGO_URL}
      alt="Core AI Summit + Tradeshow"
      style={{ height: "50px", width: "auto", display: "block" }}
    />
  </Link>
);

/* ── Desktop Dropdown Item ──────────────────────── */
function NavDropdown({ item }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const timerRef = useRef(null);

  const isActive = item.children.some(
    (c) => location.pathname === c.href.split("#")[0] || location.pathname.startsWith(c.href.split("#")[0])
  );

  const handleMouseEnter = () => {
    clearTimeout(timerRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <button
        className="flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors duration-150"
        style={{
          color: isActive || open ? "#00D4FF" : "rgba(255,255,255,0.75)",
          fontFamily: "var(--font-body)",
          background: "none",
          border: "none",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {item.label}
        <ChevronDown
          size={13}
          style={{
            color: isActive || open ? "#00D4FF" : "rgba(255,255,255,0.45)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            className="absolute top-full left-0 pt-1 z-50"
            style={{ minWidth: "210px" }}
          >
            <div
              className="py-2 rounded-xl overflow-hidden"
              style={{
                background: "rgba(9,13,22,0.97)",
                border: "1px solid rgba(255,255,255,0.09)",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
              }}
            >
              {item.children.map((child) => (
                <Link
                  key={child.label}
                  to={child.href}
                  className="block px-5 py-2.5 text-sm font-bold transition-colors whitespace-nowrap"
                  style={{ fontFamily: "var(--font-heading)", color: "white" }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#00D4FF"; e.currentTarget.style.background = "rgba(0,212,255,0.05)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "white"; e.currentTarget.style.background = "transparent"; }}
                  data-testid={`nav-submenu-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {child.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Mobile Accordion Item ──────────────────────── */
function MobileNavItem({ item, onLinkClick }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3.5"
        style={{ background: "none", border: "none", cursor: "pointer" }}
        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <span className="text-sm font-semibold text-white" style={{ fontFamily: "var(--font-body)" }}>
          {item.label}
        </span>
        <ChevronDown
          size={15}
          style={{
            color: "rgba(255,255,255,0.4)",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
          >
            <div className="pb-2 pl-4 pr-4">
              {item.children.map((child) => (
                <Link
                  key={child.label}
                  to={child.href}
                  onClick={onLinkClick}
                  className="block py-2.5 text-sm font-bold transition-colors"
                  style={{ color: "rgba(255,255,255,0.6)", fontFamily: "var(--font-heading)", paddingLeft: "12px", borderLeft: "2px solid rgba(0,212,255,0.2)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#00D4FF"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.6)"; }}
                  data-testid={`mobile-submenu-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {child.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Main Navbar ────────────────────────────────── */
export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <>
      <header
        data-testid="navbar"
        className="relative w-full z-auto"
        style={{
          background: "transparent",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
          transition: "border-color 0.3s ease",
        }}
      >
        <div className="container-xl">
          <div className="flex items-center justify-between h-16">
            <CoreAILogo />

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-0" aria-label="Main navigation">
              {NAV_ITEMS.map((item) => (
                <NavDropdown key={item.label} item={item} />
              ))}
            </nav>

            {/* CTA button — violet pill */}
            <div className="hidden lg:flex items-center">
              <button
                onClick={() => setShowModal(true)}
                className="font-bold text-white transition-all"
                style={{
                  background: "var(--violet)",
                  borderRadius: "100px",
                  padding: "10px 22px",
                  fontSize: "14px",
                  fontFamily: "var(--font-body)",
                  border: "none",
                  cursor: "pointer",
                  letterSpacing: "0",
                  transition: "background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#9239D9"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(123,47,190,0.4)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--violet)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                data-testid="nav-register-btn"
              >
                Register For 2027
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 transition-colors"
              style={{ color: "rgba(255,255,255,0.7)", background: "none", border: "none", cursor: "pointer" }}
              data-testid="nav-mobile-menu-btn"
              aria-label="Toggle mobile menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="lg:hidden overflow-hidden"
              style={{ borderTop: "1px solid rgba(255,255,255,0.06)", background: "rgba(9,13,22,0.97)" }}
              data-testid="nav-mobile-menu"
            >
              <div className="container-xl">
                {NAV_ITEMS.map((item) => (
                  <MobileNavItem
                    key={item.label}
                    item={item}
                    onLinkClick={() => setMobileOpen(false)}
                  />
                ))}
                <div className="py-4">
                  <button
                    onClick={() => { setShowModal(true); setMobileOpen(false); }}
                    className="w-full font-bold text-white text-sm py-3 rounded-full"
                    style={{ background: "var(--violet)", border: "none", cursor: "pointer", fontFamily: "var(--font-body)" }}
                    data-testid="mobile-register-btn"
                  >
                    Register For 2027
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <PriorityListModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}
